import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, RefreshCw, AlertCircle, CheckCircle, Clock, Loader, MessageSquare, Activity, Server, Trash2 } from 'lucide-react';
import api from '../services/api';
import { format } from 'date-fns';

interface WorkflowDetailModalProps {
  workflowId: string;
  onClose: () => void;
}

interface StepStatus {
  status: string;
  started_at: string;
  completed_at?: string;
  error?: string;
  progress?: number;
}

interface Workflow {
  id: string;
  customer_id: string;
  status: string;
  current_step: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  error_message?: string;
  data: any;
  spreadsheet_url?: string;
  admin_account: string;
  total_accounts: number;
  processed_accounts: number;
  total_work_units: number;
  completed_work_units: number;
  steps?: Record<string, StepStatus>;
}

const WorkflowDetailModal: React.FC<WorkflowDetailModalProps> = ({ workflowId, onClose }) => {
  const [smsCode, setSmsCode] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [showSmsVerification, setShowSmsVerification] = useState(false);
  const queryClient = useQueryClient();

  const { data: workflow, isLoading } = useQuery<Workflow>({
    queryKey: ['workflow', workflowId],
    queryFn: () => api.getWorkflowDetail(workflowId),
    refetchInterval: 3000,
  });

  const retryMutation = useMutation({
    mutationFn: () => api.retryWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.cancelWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      onClose();
    },
  });

  const smsVerificationMutation = useMutation({
    mutationFn: (data: { account: string; code: string }) =>
      api.submitSmsVerification(workflowId, data),
    onSuccess: () => {
      setShowSmsVerification(false);
      setSmsCode('');
      setSelectedAccount('');
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
    },
  });

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'in_progress':
        return <Loader className="w-5 h-5 text-orange-400 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-500/30 bg-green-500/10';
      case 'failed':
        return 'border-red-500/30 bg-red-500/10';
      case 'in_progress':
        return 'border-orange-500/30 bg-orange-500/10';
      default:
        return 'border-gray-700 bg-gray-800/50';
    }
  };

  const workflowSteps = [
    { key: 'DOMAIN_PROCESSING', label: 'Domain Setup', icon: Server },
    { key: 'IMAP_PROCESSING', label: 'IMAP Configuration', icon: Activity },
    { key: 'WARMUP_PROCESSING', label: 'Email Warmup', icon: MessageSquare },
    { key: 'QUALITY_CHECK', label: 'Quality Check', icon: CheckCircle },
  ];

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <Loader className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div>
            <h2 className="text-2xl font-bold text-white">Workflow Details</h2>
            <p className="text-sm text-gray-400 mt-1">ID: {workflowId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Status Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="stat-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Status</div>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${
                workflow?.status === 'COMPLETED' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                workflow?.status === 'FAILED' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                workflow?.status === 'PROCESSING' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                'bg-blue-500/20 text-blue-400 border-blue-500/30'
              }`}>
                {workflow?.status}
              </div>
            </div>
            <div className="stat-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Current Phase</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-500 to-yellow-500 transition-all duration-300"
                    style={{
                      width: `${workflow && workflow.total_accounts > 0
                        ? (workflow.processed_accounts / workflow.total_accounts) * 100
                        : 0}%`
                    }}
                  />
                </div>
                <span className="text-sm text-white font-medium">
                  {workflow?.processed_accounts}/{workflow?.total_accounts}
                </span>
              </div>
            </div>
            <div className="stat-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Admin Account</div>
              <div className="text-sm text-white">{workflow?.admin_account || 'N/A'}</div>
            </div>
          </div>

          {/* Overall Progress */}
          <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400">Overall Workflow Progress</h3>
                <p className="text-xs text-gray-500 mt-1">Completion across all phases</p>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
                {workflow && workflow.total_work_units > 0
                  ? Math.round((workflow.completed_work_units / workflow.total_work_units) * 100)
                  : 0}%
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-800 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-yellow-500 transition-all duration-500 ease-out"
                  style={{
                    width: `${workflow && workflow.total_work_units > 0
                      ? (workflow.completed_work_units / workflow.total_work_units) * 100
                      : 0}%`
                  }}
                />
              </div>
              <span className="text-sm text-white font-medium whitespace-nowrap">
                {workflow?.completed_work_units || 0}/{workflow?.total_work_units || 0} units
              </span>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-gray-800/30 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">Customer Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Customer ID:</span>
                <span className="text-white ml-2">{workflow?.customer_id}</span>
              </div>
              {workflow?.data?.name && (
                <div>
                  <span className="text-gray-500">Name:</span>
                  <span className="text-white ml-2">{workflow.data.name}</span>
                </div>
              )}
              {workflow?.data?.email && (
                <div>
                  <span className="text-gray-500">Email:</span>
                  <span className="text-white ml-2">{workflow.data.email}</span>
                </div>
              )}
              {workflow?.spreadsheet_url && (
                <div className="col-span-2">
                  <span className="text-gray-500">Spreadsheet:</span>
                  <a 
                    href={workflow.spreadsheet_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-orange-400 ml-2 hover:underline"
                  >
                    View Spreadsheet
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Workflow Steps */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Workflow Steps</h3>
            <div className="space-y-3">
              {workflowSteps.map((step) => {
                const stepStatus = workflow?.steps?.[step.key];
                const Icon = step.icon;
                return (
                  <div 
                    key={step.key} 
                    className={`p-4 rounded-lg border ${getStepColor(stepStatus?.status || 'pending')}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-gray-400" />
                        <div>
                          <div className="font-medium text-white">{step.label}</div>
                          {stepStatus?.started_at && (
                            <div className="text-xs text-gray-500">
                              Started: {format(new Date(stepStatus.started_at), 'MMM dd, HH:mm:ss')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {stepStatus?.progress !== undefined && stepStatus.progress > 0 && (
                          <span className="text-sm text-gray-400">{stepStatus.progress}%</span>
                        )}
                        {getStepIcon(stepStatus?.status || 'pending')}
                      </div>
                    </div>
                    {stepStatus?.error && (
                      <div className="mt-2 p-2 bg-red-500/10 rounded text-red-400 text-sm">
                        {stepStatus.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-gray-800/30 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">Timeline</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created:</span>
                <span className="text-white">{format(new Date(workflow?.created_at || ''), 'MMM dd, yyyy HH:mm:ss')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated:</span>
                <span className="text-white">{format(new Date(workflow?.updated_at || ''), 'MMM dd, yyyy HH:mm:ss')}</span>
              </div>
              {workflow?.completed_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Completed:</span>
                  <span className="text-white">{format(new Date(workflow.completed_at), 'MMM dd, yyyy HH:mm:ss')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {workflow?.error_message && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-red-400 mb-1">Error Details</div>
                  <div className="text-sm text-red-300">{workflow.error_message}</div>
                </div>
              </div>
            </div>
          )}

          {/* SMS Verification Section */}
          {showSmsVerification && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-orange-400 mb-3">SMS Verification Required</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Select Account
                  </label>
                  <select
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                  >
                    <option value="">Choose account...</option>
                    {workflow?.data?.users?.map((user: any) => (
                      <option key={user.email} value={user.email}>{user.email}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    placeholder="Enter SMS code"
                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => smsVerificationMutation.mutate({ account: selectedAccount, code: smsCode })}
                    disabled={!selectedAccount || !smsCode || smsVerificationMutation.isPending}
                    className="btn-primary text-sm"
                  >
                    {smsVerificationMutation.isPending ? 'Verifying...' : 'Submit Code'}
                  </button>
                  <button
                    onClick={() => setShowSmsVerification(false)}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center p-6 border-t border-gray-800">
          <div className="flex gap-2">
            {workflow?.status === 'PROCESSING' && workflow?.current_step === 'IMAP_PROCESSING' && (
              <button
                onClick={() => setShowSmsVerification(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                SMS Verification
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {workflow?.status === 'FAILED' && (
              <button
                onClick={() => retryMutation.mutate()}
                disabled={retryMutation.isPending}
                className="btn-secondary flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {retryMutation.isPending ? 'Retrying...' : 'Retry'}
              </button>
            )}
            {(workflow?.status === 'PROCESSING' || workflow?.status === 'INITIATED') && (
              <button
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
                className="btn-secondary text-red-400 border-red-500/50 hover:bg-red-500/20 flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
              </button>
            )}
            <button
              onClick={() => {
                if (confirm('Are you sure you want to delete this workflow? This will remove all data including events and steps.')) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="btn-secondary text-red-400 border-red-500/50 hover:bg-red-500/20 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowDetailModal;